import React from "react";
import { isMacPlatform } from "../../../services/commands/commands.types";
import { formatKeyChord, spokenKeyChord } from "../../../utils/formatKeyChord";
import { Chord, JoinedKey, Key, Keys, Separator } from "./ShortcutKeys.styles";
import type { KeyChord } from "../../../services/commands/commands.types";
import type { ShortcutKeysProps } from "./ShortcutKeys.types";

/** Glyphs that read better than the key's name on a chip. */
const CAP_GLYPHS: Record<string, string> = { Enter: "↵" };
const capOf = (cap: string): string => CAP_GLYPHS[cap] ?? cap;
const chordText = (chord: KeyChord, isMac: boolean): string => formatKeyChord(chord, isMac).map((cap) => capOf(cap.cap)).join(isMac ? "" : "+");

/** A keyboard shortcut as one chip per chord (`<kbd>`), read out as one phrase: "Command K". */
export const ShortcutKeys: React.FC<ShortcutKeysProps> = ({ chords, mac, joined = true, merged = false, className }) => {
  if (chords.length === 0) return null;
  const isMac = mac ?? isMacPlatform();
  const label = chords.map((chord) => spokenKeyChord(chord, isMac)).join(", or ");
  if (merged) {
    return (
      <Keys className={className} role="group" aria-label={label}>
        <Chord aria-hidden="true">
          <JoinedKey>{chords.map((chord) => chordText(chord, isMac)).join("")}</JoinedKey>
        </Chord>
      </Keys>
    );
  }
  return (
    <Keys className={className} role="group" aria-label={label}>
      {chords.map((chord, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={index}>
          {index > 0 ? <Separator aria-hidden="true">/</Separator> : null}
          <Chord aria-hidden="true">
            {joined ? (
              <JoinedKey>{chordText(chord, isMac)}</JoinedKey>
            ) : (
              formatKeyChord(chord, isMac).map((cap, capIndex) => (
                // eslint-disable-next-line react/no-array-index-key
                <Key key={capIndex}>{cap.cap}</Key>
              ))
            )}
          </Chord>
        </React.Fragment>
      ))}
    </Keys>
  );
};

export default ShortcutKeys;
